/**
 * Connected apps (Figma 09 · Connected apps 01–11): Claude, ChatGPT, other MCP
 * tools, and API keys. Both connectors sign in over the same MCP OAuth and land
 * on the web app's Authorize page (/connect/authorize). Not built, no backend
 * yet: a connector's "Connected" status and Disconnect (no endpoint lists a
 * person's MCP sign-ins).
 * The backend keeps keys whole, so "Creo keeps its last four characters" is
 * not said here.
 */
import { useState, type ReactNode } from "react";
import { Check, Copy, ExternalLink, KeyRound, Plus, Trash2, Unplug } from "lucide-react";
import { getMcpServerUrl } from "@/lib/mcp";
import ClaudeLogo from "@/components/icons/ClaudeLogo";
import OpenAiLogo from "@/components/icons/OpenAiLogo";
import {
  useApiKeys,
  useCreateApiKey,
  useDeleteApiKey,
  type ApiKeyListItem,
} from "@/app/routes/dashboard/settings/api/useApiKeys";
import { KitButton } from "../../kit/Button";
import { KitInput } from "../../kit/Input";
import { kitLabelClass } from "../../kit/Field";
import { KitModal } from "../../kit/Modal";
import { ErrorState } from "../../ViewStates";
import {
  ConfirmDialog,
  DetailCard,
  GlyphTile,
  DetailHead,
  DialogFooter,
  Fact,
  Facts,
  GroupLabel,
  IndexCard,
  IndexRow,
  Note,
  PageHead,
  QuietNote,
  SectionHead,
  SettingsPage,
  Split,
  SplitLoading,
  TextAction,
} from "../parts/SettingsParts";
import { useYouHead } from "../parts/scope";
import { formatDay, formatMoment, usedLabel } from "../parts/settingsFormat";

type Selection = { kind: "claude" } | { kind: "chatgpt" } | { kind: "mcp" } | { kind: "key"; id: string };

const CLAUDE_CONNECTORS = "https://claude.ai/customize/connectors?modal=add-custom-connector";
const CHATGPT_PLUGINS = "https://chatgpt.com/plugins";

const AppTile = GlyphTile;

/** An app's own mark on the solid disc. */
function LogoTile({ size = 36, Logo }: { size?: 36 | 56; Logo: typeof ClaudeLogo }) {
  return (
    <span
      aria-hidden
      style={{ width: size, height: size }}
      className="grid shrink-0 place-items-center rounded-full border border-co-line2 bg-co-surface-solid"
    >
      <Logo className={size === 56 ? "size-6" : "size-4"} />
    </span>
  );
}

const ClaudeTile = ({ size }: { size?: 36 | 56 }) => <LogoTile size={size} Logo={ClaudeLogo} />;
const ChatGptTile = ({ size }: { size?: 36 | 56 }) => <LogoTile size={size} Logo={OpenAiLogo} />;

function maskKey(key: string): string {
  return `${key.slice(0, 3)}••••••••${key.slice(-4)}`;
}

function useCopy() {
  const [copied, setCopied] = useState<string | null>(null);
  const copy = async (id: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(id);
      window.setTimeout(() => setCopied((c) => (c === id ? null : c)), 2000); // M18: "Copied" for 2 seconds
    } catch {
      // Clipboard blocked: the value is on screen to select by hand.
    }
  };
  return { copied, copy };
}

function AddressField({ label = "MCP server address" }: { label?: string }) {
  const url = getMcpServerUrl();
  const { copied, copy } = useCopy();
  return (
    <div className="flex w-full flex-col gap-2">
      <p className={kitLabelClass}>{label}</p>
      <div className="co-glass-control flex min-h-11 items-center gap-2.5 rounded-card px-3.5">
        <code className="min-w-0 flex-1 truncate font-mono text-[13px] text-co-text">{url}</code>
      </div>
      <div className="flex flex-wrap gap-2">
        <KitButton variant="secondary" iconLeft={copied ? Check : Copy} onClick={() => void copy("address", url)}>
          {copied ? "Copied" : "Copy address"}
        </KitButton>
      </div>
    </div>
  );
}

function CopyMcpUrl() {
  const url = getMcpServerUrl();
  const { copied, copy } = useCopy();
  return (
    <button
      type="button"
      onClick={() => void copy("mcp-url", url)}
      aria-label={copied ? "Copied MCP URL" : "Copy MCP URL"}
      className="inline-flex items-center gap-1 align-baseline font-bold text-co-accent underline decoration-co-accent underline-offset-[3px]"
    >
      MCP URL
      {copied ? <Check className="size-3.5 shrink-0" aria-hidden /> : <Copy className="size-3.5 shrink-0" aria-hidden />}
    </button>
  );
}

function ConnectorSteps({ title, steps }: { title: string; steps: { step: string; body: ReactNode }[] }) {
  return (
    <>
      <SectionHead title={title} meta={`${steps.length} steps`} />
      <ol className="m-0 flex list-none flex-col p-0">
        {steps.map((s, i) => (
          <li key={s.step} className="flex items-start gap-4 py-[9px]">
            <span className="w-[150px] shrink-0 text-[12.5px] text-co-muted">
              <span className="mr-2 font-mono text-co-faint">{i + 1}</span>
              {s.step}
            </span>
            <span className="min-w-0 text-[13.5px] font-medium leading-[1.45] text-co-text">{s.body}</span>
          </li>
        ))}
      </ol>
    </>
  );
}

function OpenAppLink({ href, children }: { href: string; children: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="unstyled-link inline-flex h-8 items-center gap-1.5 self-start rounded-pill px-1 text-[13px] font-medium text-co-accent hover:text-co-accent-ink"
    >
      <ExternalLink aria-hidden className="size-3.5" />
      {children}
    </a>
  );
}

function ClaudeDetail() {
  return (
    <DetailCard>
      <DetailHead lead={<ClaudeTile size={56} />} title="Claude" sub="A custom connector in Claude" />
      <ConnectorSteps
        title="Connect Claude"
        steps={[
          {
            step: "In Claude",
            body: (
              <>
                Open{" "}
                <a
                  href={CLAUDE_CONNECTORS}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="unstyled-link text-co-accent underline decoration-co-accent underline-offset-[3px] hover:text-co-accent-ink"
                >
                  Claude Connectors
                </a>
              </>
            ),
          },
          {
            step: "Name",
            body: (
              <>
                Call it <span className="font-bold text-co-accent">Creo</span> and paste the <CopyMcpUrl />
              </>
            ),
          },
          {
            step: "Continue",
            body: (
              <>
                Click <span className="font-bold">Continue</span>
              </>
            ),
          },
        ]}
      />
      <AddressField />
      <OpenAppLink href={CLAUDE_CONNECTORS}>Open Claude</OpenAppLink>
      <QuietNote>
        Claude acts as you, with your role in each workspace, and never sees your password. To stop it, remove the
        connector in Claude. Another MCP tool can use a key from API keys as the Bearer token.
      </QuietNote>
    </DetailCard>
  );
}

function ChatGptDetail() {
  return (
    <DetailCard>
      <DetailHead lead={<ChatGptTile size={56} />} title="ChatGPT" sub="A custom connector in ChatGPT" />
      <ConnectorSteps
        title="Connect ChatGPT"
        steps={[
          {
            step: "In ChatGPT",
            body: (
              <>
                Open{" "}
                <a
                  href={CHATGPT_PLUGINS}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="unstyled-link text-co-accent underline decoration-co-accent underline-offset-[3px] hover:text-co-accent-ink"
                >
                  ChatGPT Plugins
                </a>
              </>
            ),
          },
          {
            step: "Add",
            body: (
              <>
                Click <span className="font-bold">Add</span>, then{" "}
                <span className="font-bold">Add Custom MCP Server</span>
              </>
            ),
          },
          {
            step: "Name",
            body: (
              <>
                Call it <span className="font-bold text-co-accent">Creo</span> and paste the <CopyMcpUrl />
              </>
            ),
          },
          { step: "Sign in", body: "ChatGPT sends you here to allow it" },
        ]}
      />
      <AddressField />
      <OpenAppLink href="https://chatgpt.com/plugins">Open ChatGPT</OpenAppLink>
      <QuietNote>
        ChatGPT acts as you, with your role in each workspace, and never sees your password. To stop it, delete the
        connector in ChatGPT.
      </QuietNote>
    </DetailCard>
  );
}

function OtherToolsDetail({ onNewKey }: { onNewKey: () => void }) {
  return (
    <DetailCard>
      <DetailHead lead={<AppTile icon={KeyRound} />} title="Other MCP tools" sub="Connect with an API key" />
      <Facts>
        <Fact label="Use it">Add the Creo MCP server to your tool, with an API key as the Bearer token.</Fact>
        <Fact label="Acts as">You, with your role in each workspace</Fact>
      </Facts>
      <AddressField />
      <KitButton iconLeft={Plus} className="self-start" onClick={onNewKey}>
        New key
      </KitButton>
    </DetailCard>
  );
}

function KeyDetail({
  apiKey,
  revealed,
  onDone,
  onDelete,
}: {
  apiKey: ApiKeyListItem;
  revealed: boolean;
  onDone: () => void;
  onDelete: () => void;
}) {
  const { copied, copy } = useCopy();
  return (
    <DetailCard>
      <DetailHead
        lead={<AppTile icon={KeyRound} />}
        title={apiKey.name}
        sub={revealed ? "API key · made just now" : `API key · ${maskKey(apiKey.key)}`}
      />
      {revealed ? (
        <>
          <Note tone="alert">
            This is the only time Creo shows the whole key. Copy it now. If you lose it, make a new key.
          </Note>
          <div className="flex w-full flex-col gap-2">
            <p className={kitLabelClass}>Your key</p>
            <div className="co-glass-control flex min-h-11 items-center rounded-card px-3.5">
              <code className="min-w-0 flex-1 break-all font-mono text-[13px] text-co-text">{apiKey.key}</code>
            </div>
            <div className="flex gap-2">
              <KitButton iconLeft={copied ? Check : Copy} onClick={() => void copy("key", apiKey.key)}>
                {copied ? "Copied" : "Copy key"}
              </KitButton>
              <KitButton variant="ghost" onClick={onDone}>
                Done
              </KitButton>
            </div>
          </div>
        </>
      ) : null}
      <Facts>
        <Fact label="Made">{formatDay(apiKey.createdAt)}, by you</Fact>
        {!revealed ? <Fact label="Last used">{apiKey.lastUsedAt ? formatMoment(apiKey.lastUsedAt) : "Never"}</Fact> : null}
        <Fact label="Acts as">You, with your role in each workspace</Fact>
        {!revealed ? <Fact label="Shown">Only once, when it was made.</Fact> : null}
      </Facts>
      {!revealed ? (
        <>
          <SectionHead title="Use it" meta="MCP" />
          <QuietNote>Add the Creo MCP server to your tool, with this key as the Bearer token.</QuietNote>
          <AddressField />
          <TextAction icon={Trash2} tone="danger" onPress={onDelete}>
            Delete key
          </TextAction>
        </>
      ) : null}
    </DetailCard>
  );
}

export default function ConnectedAppsPage() {
  const head = useYouHead();
  const { data: keys, isLoading, isError, refetch } = useApiKeys();
  const createKey = useCreateApiKey();
  const deleteKey = useDeleteApiKey();
  const [picked, setPicked] = useState<Selection>({ kind: "claude" });
  const [revealedId, setRevealedId] = useState<string | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<ApiKeyListItem | null>(null);

  const list = keys ?? [];
  const selectedKey = picked.kind === "key" ? list.find((k) => k.id === picked.id) : undefined;
  const selection: Selection = picked.kind === "key" && !selectedKey ? { kind: "claude" } : picked;

  const status = revealedId
    ? "Copy the key now. Creo shows it once."
    : "Use Creo from Claude, ChatGPT and other tools. An app acts as you, with your role in each workspace.";

  const makeKey = (e: React.FormEvent) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    createKey.mutate(name, {
      onSuccess: (created) => {
        setRevealedId(created.id);
        setPicked({ kind: "key", id: created.id });
      },
    });
    setNewOpen(false);
    setNewName("");
  };

  return (
    <SettingsPage>
      <PageHead
        {...head}
        title="Connected apps"
        status={status}
      />
      {isLoading ? (
        <SplitLoading caption="Loading your apps and keys" />
      ) : isError ? (
        <ErrorState
          title="Your apps and keys did not load"
          body="Creo could not read them. Your connectors and keys keep working."
          onRetry={() => void refetch()}
        />
      ) : (
        <Split
          backLabel="Connected apps"
          index={
            <IndexCard>
              <GroupLabel>Apps</GroupLabel>
              <IndexRow
                lead={<ClaudeTile />}
                title="Claude"
                sub="Custom connector"
                active={selection.kind === "claude"}
                onPress={() => setPicked({ kind: "claude" })}
              />
              <IndexRow
                lead={<ChatGptTile />}
                title="ChatGPT"
                sub="Custom connector"
                active={selection.kind === "chatgpt"}
                onPress={() => setPicked({ kind: "chatgpt" })}
              />
              <IndexRow
                lead={<AppTile icon={Unplug} />}
                title="Other MCP tools"
                sub="Connect with an API key"
                active={selection.kind === "mcp"}
                onPress={() => setPicked({ kind: "mcp" })}
              />
              <GroupLabel>API keys</GroupLabel>
              {list.length === 0 ? <p className="px-3 py-2 text-[12.5px] text-co-muted">No keys yet.</p> : null}
              {list.map((k) => (
                <IndexRow
                  key={k.id}
                  lead={<AppTile icon={KeyRound} />}
                  title={k.name}
                  sub={k.id === revealedId ? "Made just now" : `Ends in ${k.key.slice(-4)} · ${usedLabel(k.lastUsedAt)}`}
                  active={selection.kind === "key" && selection.id === k.id}
                  onPress={() => setPicked({ kind: "key", id: k.id })}
                />
              ))}
              <div className="px-2 pt-1">
                <TextAction
                  icon={Plus}
                  onPress={() => {
                    setNewName("");
                    setNewOpen(true);
                  }}
                >
                  New key
                </TextAction>
              </div>
            </IndexCard>
          }
          detail={
            selection.kind === "claude" ? (
              <ClaudeDetail />
            ) : selection.kind === "chatgpt" ? (
              <ChatGptDetail />
            ) : selection.kind === "mcp" ? (
              <OtherToolsDetail onNewKey={() => setNewOpen(true)} />
            ) : selectedKey ? (
              <KeyDetail
                apiKey={selectedKey}
                revealed={selectedKey.id === revealedId}
                onDone={() => setRevealedId(null)}
                onDelete={() => setDeleteTarget(selectedKey)}
              />
            ) : null
          }
        />
      )}

      {newOpen ? (
        <KitModal
          isOpen
          onOpenChange={(open) => (open ? undefined : setNewOpen(false))}
          title="Make a new key"
          description="A key lets another tool use Creo as you. Give it a name you will recognise later."
          modalClassName="!w-[min(calc(100vw-2rem),480px)] !max-w-[480px]"
        >
          <form onSubmit={makeKey} className="flex w-full flex-col gap-3">
            <KitInput label="Name" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Tool name" autoFocus />
            <QuietNote>
              The key acts as you, with your role in each workspace. You see the whole key once, right after you make
              it.
            </QuietNote>
            <DialogFooter type="submit" confirmLabel="Make key" onCancel={() => setNewOpen(false)} disabled={!newName.trim()} />
          </form>
        </KitModal>
      ) : null}

      {deleteTarget ? (
        <ConfirmDialog
          title={`Delete the key “${deleteTarget.name}”?`}
          description="Anything that uses this key stops working at once."
          consequences={[
            {
              icon: Unplug,
              text: deleteTarget.lastUsedAt
                ? `Its tool loses its connection to Creo, last used ${formatMoment(deleteTarget.lastUsedAt).toLowerCase()}.`
                : "It has never been used.",
            },
            { icon: KeyRound, text: "A deleted key cannot come back. Make a new one if you need it again." },
          ]}
          confirmLabel="Delete key"
          onClose={() => setDeleteTarget(null)}
          onConfirm={() => {
            deleteKey.mutate(deleteTarget.id);
            if (revealedId === deleteTarget.id) setRevealedId(null);
            setPicked({ kind: "claude" });
            setDeleteTarget(null);
          }}
        />
      ) : null}
    </SettingsPage>
  );
}

